import Handlebars from "handlebars";

export interface TemplateTagValue {
  tagKey: string;
  tagValue: string;
}

let handlebarsInitialized = false;

const initHandlebars = () => {
  if (handlebarsInitialized) return;
  handlebarsInitialized = true;

  Handlebars.registerHelper("eq", function (this: any, a: any, b: any) {
    return a === b;
  });

  Handlebars.registerHelper("formatNumber", function (this: any, value: any) {
    if (typeof value !== "number") return value;
    return value.toLocaleString("he-IL");
  });
};

const containsHandlebarsExpression = (value: string): boolean =>
  !!value && value.includes("{{") && value.includes("}}");

const looksLikeHtml = (value: string): boolean => {
  if (!value) return false;

  return /<[^>]+>/.test(value);
};
const decodeHtml = (value: string): string => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const convertTagToRawHtml = (template: string, key: string): string => {
  return template
    .replace(new RegExp(`{{${key}}}`, "g"), `{{{${key}}}}`)
    .replace(new RegExp(`{{\\s+${key}\\s+}}`, "g"), `{{{${key}}}}`);
};

const convertJsonValue = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(convertJsonValue);
  }

  if (value && typeof value === "object") {
    const obj: Record<string, any> = {};

    for (const [k, v] of Object.entries(value)) {
      obj[k] = convertJsonValue(v);
    }

    return obj;
  }

  if (typeof value === "string") {
    return value.replace(/\n/g, "<br/>");
  }

  return value;
};

const isJsonValue = (value?: string): boolean => {
  if (!value) return false;

  const trimmed = value.trim();

  return (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  );
};

const buildTagValue = (value?: string): { value: any; isHtml: boolean } => {
  const raw = value ?? "";

  const decoded = decodeHtml(raw);

  if (looksLikeHtml(decoded)) {
    return {
      value: decoded,
      isHtml: true,
    };
  }

  if (looksLikeHtml(raw)) {
    return {
      value: raw,
      isHtml: true,
    };
  }

  return {
    value: decoded,
    isHtml: false,
  };
};



export const createDocumentTemplateEngine = () => {
  initHandlebars();
  const compileTagsToContext = (
    tags: TemplateTagValue[],
  ): Record<string, any> => {
    const context: Record<string, any> = {};

    for (const tag of tags) {
      const key = (tag.tagKey ?? "")
        .replace(/^\s*{{\s*/, "")
        .replace(/\s*}}\s*$/, "")
        .trim();

      if (!key) continue;

      const rawValue = tag.tagValue ?? "";

      if (isJsonValue(rawValue)) {
        try {
          context[key] = convertJsonValue(JSON.parse(rawValue));
          continue;
        } catch {
          // ממשיך כמו בשרת
        }
      }

      context[key] = buildTagValue(rawValue).value;
    }

    return context;
  };

  const renderTemplate = (
    template: string,
    context: Record<string, any>,
  ): string => {
    let templateToRender = template;

    for (const [key, value] of Object.entries(context)) {
      if (typeof value === "string" && looksLikeHtml(value)) {
        templateToRender = convertTagToRawHtml(templateToRender, key);
      }
    }

    let rendered = Handlebars.compile(templateToRender)(context);

    if (containsHandlebarsExpression(rendered)) {
      let nestedTemplate = rendered;

      for (const [key, value] of Object.entries(context)) {
        if (typeof value === "string" && looksLikeHtml(value)) {
          nestedTemplate = convertTagToRawHtml(nestedTemplate, key);
        }
      }

      rendered = Handlebars.compile(nestedTemplate)(context);
    }

    return rendered;
  };
  return {
    renderTemplate,
    compileTagsToContext,
  };
};
